/**
 * @module store/modules/supervisor/actions
 * @description Supervisor Vuex actions for monitoring agents and livechat sessions.
 */

import { graph } from "@/store/api";
import { ActionTree } from "vuex";
import { RootState } from "@/store/types";
import { SupervisorState } from "./types";
import { gql } from "@apollo/client/core";
import _ from "lodash";
import { parseSessions } from "./utils";

const PAGINGATE_LIVECHAT_QUERY = require("@/graphql/LIVECHATS_PAGINATE_SESSIONS.gql");

const actions: ActionTree<SupervisorState, RootState> = {
  /**
   * Shows or hides the agent list based on status.
   *
   * @param {Object} context - Vuex action context
   * @param {Object} context.state - Current module state
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} [payload.status] - Agent status filter
   * @returns {void}
   */
  SHOW_AGENT_LIST({ state, commit }, { status }) {
    if (state.currentExpanded) {
      commit("SET_CURRENT_EXPANDED", null);
    }
    if (!status) {
      commit("SET_CURRENT_MODE", null);
    }
  },
  /**
   * Shows the online agents list.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Function} context.dispatch - Vuex dispatch function
   * @returns {void}
   *
   * @example
   * store.dispatch('supervisor/SHOW_ONLINE_AGENTS');
   */
  SHOW_ONLINE_AGENTS({ commit, dispatch }) {
    commit("SET_CURRENT_MODE", "ONLINE_AGENTS");
    dispatch("SHOW_AGENT_LIST", { status: "online" });
  },
  /**
   * Shows the offline agents list.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Function} context.dispatch - Vuex dispatch function
   * @returns {void}
   *
   * @example
   * store.dispatch('supervisor/SHOW_OFFLINE_AGENTS');
   */
  SHOW_OFFLINE_AGENTS({ commit, dispatch }) {
    commit("SET_CURRENT_MODE", "OFFLINE_AGENTS");
    dispatch("SHOW_AGENT_LIST", { status: "offline" });
  },
  /**
   * Fetches all agents with their chat counts by department.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} [payload.email] - Optional email filter
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('supervisor/FETCH_AGENTS_WITH_CHAT_COUNT', {
   *   email: 'agent@example.com'
   * });
   */
  FETCH_AGENTS_WITH_CHAT_COUNT: async ({ commit }, { email }) => {
    try {
      commit("SET_LOADING_STATE", { type: "agentListLoading", value: true });
      const response = await graph.query({
        query: gql`
          query ($email: String) {
            livechatAPI {
              agentsWithChatCountByDepartment(email: $email)
            }
          }
        `,
        variables: {
          email,
        },
        fetchPolicy: "network-only",
      });
      const agentsWithChatCount = _.get(
        response,
        "data.livechatAPI.agentsWithChatCountByDepartment",
        {}
      );
      commit("SET_AGENTS_WITH_CHAT_COUNT", agentsWithChatCount);
    } catch (error) {
      console.error(error);
    } finally {
      commit("SET_LOADING_STATE", { type: "agentListLoading", value: false });
    }
  },

  /**
   * Fetches in-queue (unassigned) chats from the backend.
   *
   * @param {Object} context - Vuex action context
   * @param {Object} context.rootState - Root state
   * @param {Object} context.state - Current module state
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} [payload={}] - Action payload
   * @param {string} [payload.transferRedactedId] - Redacted ID for transfer
   * @param {Array} [payload.transferRedactedIdInteractions] - Interactions for transfer
   * @param {number} [payload.limit=10] - Number of chats to fetch
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('supervisor/FETCH_IN_QUEUE_CHATS', { limit: 20 });
   */
  FETCH_IN_QUEUE_CHATS: async (
    { rootState, state, commit },
    { transferRedactedId, transferRedactedIdInteractions, limit } = { limit: 10 }
  ) => {
    try {
      commit("SET_LOADING_STATE", { type: "inQueueChatLoading", value: true });

      const response = await graph.query({
        query: PAGINGATE_LIVECHAT_QUERY,
        fetchPolicy: "network-only",
        variables: {
          limit,
          agentIs: "[]",
          resolved: false,
        },
      });

      const { data, count } = _.get(response, "data.livechatAPI.paginateSessions", {
        data: [],
        count: 0,
      });

      const updatedSessionsObject = parseSessions({
        data,
        selectedChatId: _.get(state, "selectedChatId"),
        queueChatObject: rootState.livechat.queueChatObject,
        transferRedactedIdInteractions,
        transferRedactedId,
      });

      commit("SET_IN_QUEUE_CHATS", {
        data: updatedSessionsObject,
        count,
      });
    } catch (error) {
      console.error(error);
    } finally {
      commit("SET_LOADING_STATE", { type: "inQueueChatLoading", value: false });
    }
  },
  /**
   * Fetches on-going (assigned but not resolved) chats from the backend.
   *
   * @param {Object} context - Vuex action context
   * @param {Object} context.rootState - Root state
   * @param {Object} context.state - Current module state
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} [payload={}] - Action payload
   * @param {string} [payload.transferRedactedId] - Redacted ID for transfer
   * @param {Array} [payload.transferRedactedIdInteractions] - Interactions for transfer
   * @param {number} [payload.limit=10] - Number of chats to fetch
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('supervisor/FETCH_ON_GOING_CHATS', { limit: 20 });
   */
  FETCH_ON_GOING_CHATS: async (
    { rootState, state, commit },
    { transferRedactedId, transferRedactedIdInteractions, limit } = { limit: 10 }
  ) => {
    try {
      commit("SET_LOADING_STATE", { type: "onGoingChatLoading", value: true });

      const response = await graph.query({
        query: PAGINGATE_LIVECHAT_QUERY,
        fetchPolicy: "network-only",
        variables: {
          limit,
          agentIsNot: "[]",
          resolved: false,
        },
      });

      const { data, count } = _.get(response, "data.livechatAPI.paginateSessions", {
        data: [],
        count: 0,
      });

      const updatedSessionsObject = parseSessions({
        data,
        selectedChatId: _.get(state, "selectedChatId"),
        queueChatObject: rootState.livechat.queueChatObject,
        transferRedactedIdInteractions,
        transferRedactedId,
      });

      commit("SET_ON_GOING_CHATS", {
        data: updatedSessionsObject,
        count,
      });
    } catch (error) {
      console.error(error);
    } finally {
      commit("SET_LOADING_STATE", { type: "onGoingChatLoading", value: false });
    }
  },
  /**
   * Fetches abandoned (unassigned and resolved) chats from the backend.
   *
   * @param {Object} context - Vuex action context
   * @param {Object} context.rootState - Root state
   * @param {Object} context.state - Current module state
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} [payload={}] - Action payload
   * @param {string} [payload.transferRedactedId] - Redacted ID for transfer
   * @param {Array} [payload.transferRedactedIdInteractions] - Interactions for transfer
   * @param {number} [payload.limit=10] - Number of chats to fetch
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('supervisor/FETCH_ABANDONED_CHATS', { limit: 20 });
   */
  FETCH_ABANDONED_CHATS: async (
    { rootState, state, commit },
    { transferRedactedId, transferRedactedIdInteractions, limit } = { limit: 10 }
  ) => {
    try {
      commit("SET_LOADING_STATE", { type: "abandonedChatLoading", value: true });

      const response = await graph.query({
        query: PAGINGATE_LIVECHAT_QUERY,
        fetchPolicy: "network-only",
        variables: {
          limit,
          agentIs: "[]",
          resolved: true,
        },
      });

      const { data, count } = _.get(response, "data.livechatAPI.paginateSessions", {
        data: [],
        count: 0,
      });

      const updatedSessionsObject = parseSessions({
        data,
        selectedChatId: _.get(state, "selectedChatId"),
        queueChatObject: rootState.livechat.queueChatObject,
        transferRedactedIdInteractions,
        transferRedactedId,
      });

      commit("SET_ABANDONED_CHATS", {
        data: updatedSessionsObject,
        count,
      });
    } catch (error) {
      console.error(error);
    } finally {
      commit("SET_LOADING_STATE", { type: "abandonedChatLoading", value: false });
    }
  },
  /**
   * Fetches resolved (assigned and closed) chats from the backend.
   *
   * @param {Object} context - Vuex action context
   * @param {Object} context.rootState - Root state
   * @param {Object} context.state - Current module state
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} [payload={}] - Action payload
   * @param {string} [payload.transferRedactedId] - Redacted ID for transfer
   * @param {Array} [payload.transferRedactedIdInteractions] - Interactions for transfer
   * @param {number} [payload.limit=10] - Number of chats to fetch
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('supervisor/FETCH_RESOLVED_CHATS', { limit: 20 });
   */
  FETCH_RESOLVED_CHATS: async (
    { rootState, state, commit },
    { transferRedactedId, transferRedactedIdInteractions, limit } = { limit: 10 }
  ) => {
    try {
      commit("SET_LOADING_STATE", { type: "resolvedChatLoading", value: true });

      const response = await graph.query({
        query: PAGINGATE_LIVECHAT_QUERY,
        fetchPolicy: "network-only",
        variables: {
          limit,
          agentIsNot: "[]",
          resolved: true,
        },
      });

      const { data, count } = _.get(response, "data.livechatAPI.paginateSessions", {
        data: [],
        count: 0,
      });

      const updatedSessionsObject = parseSessions({
        data,
        selectedChatId: _.get(state, "selectedChatId"),
        queueChatObject: rootState.livechat.queueChatObject,
        transferRedactedIdInteractions,
        transferRedactedId,
      });

      commit("SET_RESOLVED_CHATS", {
        data: updatedSessionsObject,
        count,
      });
    } catch (error) {
      console.error(error);
    } finally {
      commit("SET_LOADING_STATE", { type: "resolvedChatLoading", value: false });
    }
  },
  /**
   * Flushes all supervisor data from the state.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('supervisor/FLUSH_SUPERVISOR_DATA');
   */
  FLUSH_SUPERVISOR_DATA: async ({ commit }) => {
    commit("SET_AGENTS_WITH_CHAT_COUNT", []);

    const initialData = { data: [], count: 0 };
    commit("SET_IN_QUEUE_CHATS", initialData);
    commit("SET_ON_GOING_CHATS", initialData);
    commit("SET_ABANDONED_CHATS", initialData);
    commit("SET_RESOLVED_CHATS", initialData);
  },
};

export default actions;
